

import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SPECIALTIES   = ['vegetables','flowers','seedlings','fruits','herbs','organic','pots','other'];
const CAT_ICONS     = { vegetables:'🥦',flowers:'🌸',seedlings:'',fruits:'🍎',herbs:'',organic:'♻️',pots:'🪴',other:'' };
const PLANT_CATS    = ['vegetables','flowers','seedlings','fruits','herbs','other'];
const UNITS         = ['plants','pots','packets','kg','bunches'];
const EMPTY_PLANT   = { name:'', category:'vegetables', stage:'', quantity:0, unit:'plants', price:0, priceUnit:'per plant', description:'', image:'' };
const EMPTY_NURSERY = { name:'', ownerName:'', mobile:'', alternateMobile:'', address:'', city:'', state:'Karnataka', pincode:'', timings:'7am – 7pm, Mon–Sat', description:'', specialties:[], adminMobile:'', hasAdminPanel:false };

const STATUS_STYLE = {
  available:   { bg:'#DCFCE7', color:'#166534', label:'In Stock' },
  low_stock:   { bg:'#FEF9C3', color:'#854D0E', label:'Low Stock' },
  out_of_stock:{ bg:'#FEE2E2', color:'#991B1B', label:'Out of Stock' },
};

const uid = () => '_' + Math.random().toString(36).slice(2);

export default function NurseryManagementPage({ tab: initialTab = 'list' }) {
  // ── Nursery state ─────────────────────────────────────────────
  const [nurseries, setNurseries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState(initialTab);
  // Sync with route prop
  React.useEffect(() => { setTab(initialTab); if(initialTab==='categories') loadCategories(); }, [initialTab]);
  const [selected,  setSelected]  = useState(null);
  const [showAdd,   setShowAdd]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [form,      setForm]      = useState(EMPTY_NURSERY);
  const [searchN,   setSearchN]   = useState('');
  const [editNursery, setEditNursery] = useState(null);
  const [editForm,    setEditForm]    = useState({});

  // ── Plant state ───────────────────────────────────────────────
  const [plants,       setPlants]       = useState([]);
  const [plantsLoading,setPlantsLoading]= useState(false);
  const [showAddPlant, setShowAddPlant] = useState(false);
  const [editPlant,    setEditPlant]    = useState(null);
  const [plantForm,    setPlantForm]    = useState(EMPTY_PLANT);
  const [filterCat,    setFilterCat]    = useState('');
  const [searchP,      setSearchP]      = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const plantImgRef = useRef();

  // ── Category state ────────────────────────────────────────────
  const [categories,   setCategories]   = useState([]);
  const [catsLoading,  setCatsLoading]  = useState(false);
  const [selCat,       setSelCat]       = useState(null);
  const [showAddCat,   setShowAddCat]   = useState(false);
  const [showAddSub,   setShowAddSub]   = useState(false);
  const [editCat,      setEditCat]      = useState(null);
  const [editSub,      setEditSub]      = useState(null);
  const [newCat,       setNewCat]       = useState({ name:'', emoji:'' });
  const [newSub,       setNewSub]       = useState({ name:'' });
  const [searchCat,    setSearchCat]    = useState('');
  const [searchSub,    setSearchSub]    = useState('');
  const [catDragIdx,   setCatDragIdx]   = useState(null);
  const [catDragOver,  setCatDragOver]  = useState(null);
  const [subDragIdx,   setSubDragIdx]   = useState(null);
  const [subDragOver,  setSubDragOver]  = useState(null);

  useEffect(() => { loadNurseries(); }, []);

  // ── Load nurseries ────────────────────────────────────────────
  const loadNurseries = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nursery?admin=true&limit=200');
      setNurseries(data.nurseries || []);
    } catch {
      setNurseries([
        { _id:'1', name:'Green Valley Nursery', ownerName:'Ravi Kumar', mobile:'9876543210', address:'Mallasandram, Bangalore', isVerified:false, isActive:true, specialties:['vegetables','flowers'] },
      ]);
    } finally { setLoading(false); }
  };

  // ── Load plants for a nursery ─────────────────────────────────
  const loadPlants = async (nursery) => {
    setPlantsLoading(true);
    try {
      const { data } = await api.get('/nursery/' + nursery._id + '/plants');
      const flat = Object.values(data.plants || {}).flat();
      setPlants(flat);
    } catch { setPlants([]); }
    finally { setPlantsLoading(false); }
  };

  // ── Load categories ───────────────────────────────────────────
  const loadCategories = async () => {
    setCatsLoading(true);
    try {
      const { data } = await api.get('/nursery/');
      const cats = (data.categories || []).map(c => ({
        ...c,
        label: c.label || c.name || c.key || '',
        subCategories: c.subCategories || [],
        isActive: c.isActive !== false,
      }));
      setCategories(cats);
      if (cats.length > 0) setSelCat(s => s || cats[0]);
    } catch (err) {
      console.error('Failed to load categories:', err.message);
    } finally { setCatsLoading(false); }
  };

  // Switch tabs
  const switchTab = (t) => {
    setTab(t);
    if (t === 'categories') loadCategories();
  };

  // ── Nursery CRUD ──────────────────────────────────────────────
  const setF = (k, v) => setForm(p => ({...p, [k]:v}));
  const toggleSpecialty = (s) => setForm(p => ({...p, specialties: p.specialties.includes(s) ? p.specialties.filter(x=>x!==s) : [...p.specialties, s]}));

  const addNursery = async () => {
    if (!form.name || !form.ownerName || !form.mobile || !form.address)
      return toast.error('Name, owner, mobile and address are required');
    setSaving(true);
    try {
      const { data } = await api.post('/nursery', form);
      setNurseries(p => [...p, data.nursery]);
      toast.success('Nursery added ✅');
      setShowAdd(false); setForm(EMPTY_NURSERY);
    } catch (err) { toast.error(err.response?.data?.message || 'Error adding nursery'); }
    finally { setSaving(false); }
  };

  const toggleVerify = async (nursery) => {
    try {
      await api.put('/nursery/' + nursery._id, { isVerified: !nursery.isVerified });
      setNurseries(p => p.map(n => n._id===nursery._id ? {...n, isVerified:!n.isVerified} : n));
      toast.success(nursery.isVerified ? 'Verification removed' : 'Nursery verified ✅');
    } catch { toast.error('Error'); }
  };

  const deleteNursery = async (id, name) => {
    if (!window.confirm('Delete "' + name + '"? All plant data will be lost.')) return;
    try {
      await api.delete('/nursery/' + id);
      setNurseries(p => p.filter(n => n._id !== id));
      toast.success('Nursery deleted');
    } catch { toast.error('Error'); }
  };

  const toggleActive = async (nursery) => {
    try {
      await api.put('/nursery/' + nursery._id, { isActive: !nursery.isActive });
      setNurseries(p => p.map(n => n._id===nursery._id ? {...n, isActive:!n.isActive} : n));
      toast.success(nursery.isActive ? 'Nursery set to Inactive' : 'Nursery set to Active ✅');
    } catch { toast.error('Error updating status'); }
  };

  const openEditNursery = (nursery) => {
    setEditForm({
      name:        nursery.name || '',
      ownerName:   nursery.ownerName || '',
      mobile:      nursery.mobile || '',
      address:     nursery.address || '',
      city:        nursery.city || '',
      pincode:     nursery.pincode || '',
      timings:     nursery.timings || '',
      description: nursery.description || '',
      specialties: nursery.specialties || [],
    });
    setEditNursery(nursery);
  };

  const saveEditNursery = async () => {
    if (!editForm.name || !editForm.ownerName) return toast.error('Name and owner required');
    setSaving(true);
    try {
      await api.put('/nursery/' + editNursery._id, editForm);
      setNurseries(p => p.map(n => n._id===editNursery._id ? {...n,...editForm} : n));
      toast.success('Nursery updated ✅');
      setEditNursery(null);
    } catch { toast.error('Error saving'); }
    finally { setSaving(false); }
  };

  // ── Plant CRUD ────────────────────────────────────────────────
  const openAddPlant  = () => { setPlantForm(EMPTY_PLANT); setEditPlant(null); setShowAddPlant(true); };
  const openEditPlant = (p)  => { setPlantForm({name:p.name,category:p.category||'vegetables',stage:p.stage||'',quantity:p.quantity||0,unit:p.unit||'plants',price:p.price||0,priceUnit:p.priceUnit||'per plant',description:p.description||'',image:p.image||''}); setEditPlant(p); setShowAddPlant(true); };

  const savePlant = async () => {
    if (!plantForm.name || !selected) return toast.error('Plant name required');
    setSaving(true);
    try {
      if (editPlant) {
        const { data } = await api.put('/nursery/' + selected._id + '/plants/' + editPlant._id, plantForm);
        setPlants(p => p.map(pl => pl._id===editPlant._id ? data.plant : pl));
        toast.success('Plant updated ✅');
      } else {
        await api.post('/nursery/' + selected._id + '/plants', plantForm);
        await loadPlants(selected);
        toast.success(plantForm.name + ' added ✅');
      }
      setShowAddPlant(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const deletePlant = async (plant) => {
    if (!window.confirm('Remove "' + plant.name + '"?')) return;
    try {
      await api.delete('/nursery/' + selected._id + '/plants/' + plant._id);
      setPlants(p => p.filter(pl => pl._id !== plant._id));
      toast.success('Removed');
    } catch { toast.error('Error'); }
  };

  const uploadPlantImg = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData(); fd.append('files', file);
      const { data } = await api.post('/upload/media', fd, { headers:{'Content-Type':'multipart/form-data'} });
      const url = data.files?.[0]?.url;
      if (url) setPlantForm(p => ({...p, image:url}));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingImg(false); }
  };

  // ── Category CRUD ─────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCat.name.trim()) return toast.error('Name required');
    setSaving(true);
    const cat = { _id:uid(), label:newCat.name, key:newCat.name.toLowerCase().replace(/\s+/g,'_'), emoji:newCat.emoji||'', subCategories:[], isActive:true };
    try { const {data}=await api.patch('/nursery-categories/owner/categories/','/categories',{label:cat.label,key:cat.key,emoji:cat.emoji}); if(data.category?._id) cat._id=data.category._id; } catch {}
    setCategories(p => [...p, cat]);
    toast.success('Category added ✅'); setShowAddCat(false); setNewCat({name:'',emoji:''}); setSaving(false);
  };

  const updateCategory = async () => {
    if (!editCat) return;
    setSaving(true);
    try {
      await api.put('/nursery/categories/' + editCat._id, { label:editCat.label, emoji:editCat.emoji });
      toast.success('Updated ✅');
    } catch { toast.error('Error updating'); }
    setEditCat(null); setSaving(false);
    loadCategories();
  };

  const toggleCat = async (cat) => {
    const upd = {...cat, isActive:!cat.isActive};
    setCategories(p => p.map(c => c._id===cat._id ? upd : c));
    if (selCat?._id===cat._id) setSelCat(upd);
    try { await api.put('/nursery/categories/' + cat._id, { isActive:!cat.isActive }); } catch {}
    toast.success(upd.isActive ? 'Enabled' : 'Disabled');
  };

  const deleteCat = async (cat) => {
    if (!window.confirm('Delete "' + (cat.label||cat.name) + '" and all its subcategories?')) return;
    setCategories(p => p.filter(c => c._id !== cat._id));
    if (selCat?._id === cat._id) setSelCat(null);
    try {
      await api.delete('/nursery/categories/' + cat._id);
      toast.success('Deleted ✅');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
      loadCategories();
    }
  };

  const addSubCategory = async () => {
    if (!newSub.name.trim() || !selCat) return toast.error('Name required');
    setSaving(true);
    try {
      await api.post('/nursery/categories/' + selCat._id + '/subcategories', { name:newSub.name, image:newSub.image||'' });
      toast.success('Subcategory added ✅');
      setShowAddSub(false); setNewSub({name:''});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error adding subcategory');
    }
    setSaving(false);
    loadCategories();
  };

  const updateSubCategory = async () => {
    if (!editSub) return;
    setSaving(true);
    try { await api.put('/nursery/categories/' + selCat._id + '/subcategories/' + editSub._id, { name:editSub.name }); } catch {}
    const subs = (selCat.subCategories||[]).map(s => s._id===editSub._id ? {...s,...editSub} : s);
    const upd = {...selCat, subCategories:subs};
    setSelCat(upd); setCategories(p => p.map(c => c._id===selCat._id ? upd : c));
    toast.success('Updated ✅'); setEditSub(null); setSaving(false);
  };

  const deleteSubCategory = async (sub) => {
    if (!window.confirm('Delete "' + sub.name + '"?')) return;
    const upd = {...selCat, subCategories:(selCat.subCategories||[]).filter(s=>s._id!==sub._id)};
    setSelCat(upd); setCategories(p => p.map(c => c._id===selCat._id ? upd : c));
    try {
      await api.delete('/nursery/categories/' + selCat._id + '/subcategories/' + sub._id);
      toast.success('Deleted ✅');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
      loadCategories();
    }
  };

  const saveCatOrder = async (cats) => {
    setCategories(cats);
    if (selCat) { const upd = cats.find(c => c._id===selCat._id); if(upd) setSelCat(upd); }
    try { await Promise.all(cats.map((c,i) => api.put('/nursery/categories/'+c._id, { order:i }))); } catch {}
  };

  const saveSubOrder = async (subs) => {
    const upd = {...selCat, subCategories:subs};
    setSelCat(upd); setCategories(p => p.map(c => c._id===selCat._id ? upd : c));
    try { await api.put('/nursery/categories/'+selCat._id, { subCategories:subs }); } catch {}
  };

  const filteredNurseries = nurseries.filter(n => {
    if (!searchN) return true;
    const q = searchN.toLowerCase().trim();
    const mobile = searchN.replace(/\D/g,'');
    return (
      (n.name||'').toLowerCase().includes(q) ||
      (n.ownerName||'').toLowerCase().includes(q) ||
      (mobile && (n.mobile||'').includes(mobile)) ||
      (n.city||'').toLowerCase().includes(q) ||
      (n.address||'').toLowerCase().includes(q)
    );
  });
  const filteredCats      = categories.filter(c => (c.label||'').toLowerCase().includes(searchCat.toLowerCase()));
  const filteredSubs      = (selCat?.subCategories||[]).filter(s => s.name.toLowerCase().includes(searchSub.toLowerCase()));
  const filteredPlants    = plants.filter(p => (!filterCat || p.category===filterCat) && (!searchP || p.name.toLowerCase().includes(searchP.toLowerCase())));

  return (
    <AdminLayout title={tab==="list" ? "🏪 Nursery List" : tab==="plants" ? " Plant Management" : tab==="categories" ? "🗂️ Category List" : "Nursery Management"}>

      {/* Title changes based on active tab */}

      {/* ══ NURSERY LIST TAB ══════════════════════════════════════ */}
      {tab === 'list' && (
        <>
          <div className="flex-between" style={{ marginBottom:14 }}>
            <input placeholder="🔍 Search name, owner, mobile, city..." value={searchN} onChange={e=>setSearchN(e.target.value)}
              style={{ padding:'8px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, outline:'none', width:260 }} />
            <button className="btn btn--primary" onClick={() => setShowAdd(true)}>➕ Add Nursery</button>
          </div>
          <div style={{ fontSize:12, color:'#6C757D', marginBottom:12 }}>{filteredNurseries.length} nurseries</div>

          {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Owner</th>
                      <th>Location</th>
                      <th>Specialties</th>
                      <th>Verified</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNurseries.map(n => (
                      <tr key={n._id}>
                        {/* <td style={{ fontWeight:700, fontSize:13 }}>{n.name}</td> */}
                        <td>
  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
    <div style={{ width:36, height:36, borderRadius:10, background:'#E8F5E9', border:'1px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:'#1A6B3C', flexShrink:0, overflow:'hidden' }}>
      {n.logo
        ? <img src={n.logo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : (n.name?.[0]?.toUpperCase() || '🏪')}
    </div>
    <span style={{ fontWeight:700, fontSize:13 }}>{n.name}</span>
  </div>
</td>
                        <td style={{ color:'#2563EB', fontSize:13 }}>+91 {n.mobile}</td>
                        <td style={{ fontSize:13 }}>{n.ownerName}</td>
                        <td style={{ fontSize:12, color:'#6C757D' }}>{n.city || n.address?.split(',')[0] || '—'}</td>
                        <td>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                            {n.specialties?.slice(0,3).map(s => (
                              <span key={s} className="badge badge--primary" style={{ textTransform:'capitalize', fontSize:10 }}>{CAT_ICONS[s]} {s}</span>
                            ))}
                            {n.specialties?.length > 3 && <span style={{ fontSize:10, color:'#9E9E9E' }}>+{n.specialties.length-3}</span>}
                          </div>
                        </td>
                        <td>
                          {n.isVerified
                            ? <span className="badge badge--success">✓ Verified</span>
                            : <span className="badge badge--warning">Pending</span>}
                        </td>
                        <td>
                          {n.isActive !== false
                            ? <span className="badge badge--success">Active</span>
                            : <span className="badge badge--danger">Inactive</span>}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:5, flexWrap:'nowrap' }}>
                            <button className="btn btn--outline btn--sm" onClick={() => { setSelected(n); loadPlants(n); switchTab('plants'); }}> Plants</button>
                            <button className="btn btn--outline btn--sm" onClick={() => openEditNursery(n)}>✏️ Edit</button>
                            <button className={'btn btn--sm ' + (n.isVerified?'btn--warning':'btn--success')} onClick={() => toggleVerify(n)}>
                              {n.isVerified ? 'Unverify' : '✓ Verify'}
                            </button>
                            <button className={'btn btn--sm ' + (n.isActive!==false?'btn--warning':'btn--success')} onClick={() => toggleActive(n)}>
                              {n.isActive!==false ? '— Inactive' : '✅ Active'}
                            </button>
                            <button className="btn btn--danger btn--sm" onClick={() => deleteNursery(n._id, n.name)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══ PLANT MANAGEMENT TAB ════════════════════════════════ */}
      {tab === 'plants' && (
        <>
          <div style={{ marginBottom:14, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <select className="input" style={{ width:260 }} value={selected?._id||''} onChange={e => { const n=nurseries.find(n=>n._id===e.target.value); setSelected(n||null); if(n) loadPlants(n); }}>
              <option value="">-- Select nursery --</option>
              {nurseries.map(n => <option key={n._id} value={n._id}>{n.name} — {n.ownerName}</option>)}
            </select>
            {selected && <button className="btn btn--primary btn--sm" onClick={openAddPlant}>➕ Add Plant</button>}
          </div>

          {!selected ? (
            <div className="empty-state">
              <div className="empty-state__icon"></div>
              <div className="empty-state__title">Select a nursery</div>
              <div className="empty-state__desc">Choose a nursery above to manage their plant stock</div>
            </div>
          ) : (
            <>
              <div style={{ background:'#F0FDF4', borderRadius:8, padding:'10px 14px', marginBottom:14, border:'1px solid #BBF7D0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, fontWeight:600, color:'#166534' }}>🏪 {selected.name} — Plant Stock</span>
                <span style={{ fontSize:12, color:'#4D7C0F' }}>{plants.length} plants</span>
              </div>

              {/* Plant filters */}
              <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                <input placeholder="🔍 Search plants..." value={searchP} onChange={e=>setSearchP(e.target.value)}
                  style={{ padding:'7px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:12, outline:'none', width:200 }} />
                {['', ...PLANT_CATS].map(c => (
                  <button key={c} onClick={() => setFilterCat(c)}
                    style={{ padding:'5px 12px', borderRadius:20, border:'1.5px solid '+(filterCat===c?'#4D7C0F':'#dee2e6'), background:filterCat===c?'#4D7C0F':'white', color:filterCat===c?'white':'#6C757D', fontSize:11, cursor:'pointer', textTransform:'capitalize', whiteSpace:'nowrap' }}>
                    {c||'All'}
                  </button>
                ))}
              </div>

              {/* Plants table */}
              {plantsLoading ? <div className="page-loader"><span className="spinner spinner--md" /></div> :
              filteredPlants.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon"></div>
                  <div className="empty-state__title">{plants.length===0 ? 'No plants yet' : 'No results'}</div>
                  {plants.length===0 && <button className="btn btn--primary" style={{ marginTop:12 }} onClick={openAddPlant}>Add first plant</button>}
                </div>
              ) : (
                <div className="card">
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>Plant</th><th>Category</th><th>Stage</th><th>Price</th><th>Stock</th><th>Status</th><th>Actions</th>
                      </tr></thead>
                      <tbody>
                        {filteredPlants.map(plant => {
                          const st = STATUS_STYLE[plant.status] || STATUS_STYLE.available;
                          return (
                            <tr key={plant._id}>
                              <td>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <div style={{ width:32, height:32, borderRadius:7, background:'#F0FDF4', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, border:'1px solid #BBF7D0' }}>
                                    {plant.image ? <img src={plant.image} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : ''}
                                  </div>
                                  <span style={{ fontWeight:600, fontSize:13 }}>{plant.name}</span>
                                </div>
                              </td>
                              <td style={{ textTransform:'capitalize', fontSize:12, color:'#6C757D' }}>{plant.category}</td>
                              <td style={{ fontSize:12, color:'#9E9E9E' }}>{plant.stage||'—'}</td>
                              <td style={{ fontWeight:600, color:'#166534' }}>
                                {plant.price>0 ? '₹'+plant.price : '—'}
                                {plant.price>0 && <div style={{ fontSize:10, color:'#9E9E9E', fontWeight:400 }}>{plant.priceUnit}</div>}
                              </td>
                              <td style={{ fontWeight:600 }}>{plant.quantity||0} <span style={{ fontSize:11, color:'#9E9E9E', fontWeight:400 }}>{plant.unit}</span></td>
                              <td><span className="badge" style={{ background:st.bg, color:st.color, fontSize:10 }}>{st.label}</span></td>
                              <td>
                                <div style={{ display:'flex', gap:5 }}>
                                  <button className="btn btn--outline btn--sm" onClick={() => openEditPlant(plant)}>✏️</button>
                                  <button className="btn btn--danger btn--sm"  onClick={() => deletePlant(plant)}>🗑</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ══ CATEGORY LIST TAB ════════════════════════════════════ */}
      {tab === 'categories' && (
        <>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
            {[
              { label:'Total Categories', value:categories.length,                                    border:'#2E7D32' },
              { label:'Sub-categories',   value:categories.reduce((s,c)=>s+(c.subCategories?.length||0),0), border:'#1565C0' },
              { label:'Active',           value:categories.filter(c=>c.isActive!==false).length,      border:'#E65100' },
              { label:'Disabled',         value:categories.filter(c=>c.isActive===false).length,      border:'#C62828' },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{ borderLeft:'4px solid '+s.border }}>
                <div className="stat-card__label">{s.label}</div>
                <div className="stat-card__value" style={{ fontSize:24 }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:16 }}>
            {/* Left: Category list */}
            <div>
              <div className="flex-between" style={{ marginBottom:8 }}>
                <span style={{ fontWeight:600, fontSize:13, color:'#1B5E20' }}>Categories</span>
                <button className="btn btn--primary btn--sm" onClick={() => setShowAddCat(true)}>＋ Add</button>
              </div>
              <input value={searchCat} onChange={e=>setSearchCat(e.target.value)} placeholder="Search..."
                style={{ width:'100%', padding:'7px 10px', border:'1px solid #dee2e6', borderRadius:7, fontSize:12, outline:'none', marginBottom:8, boxSizing:'border-box' }} />
              {catsLoading ? <div style={{ textAlign:'center', padding:20 }}><span className="spinner" /></div> :
                filteredCats.map((cat, idx) => {
                  const isDrg = catDragIdx===idx, isDrgOv = catDragOver===idx;
                  return (
                  <div key={cat._id}
                    draggable
                    onDragStart={e => { e.dataTransfer.effectAllowed='move'; setCatDragIdx(idx); }}
                    onDragOver={e => { e.preventDefault(); if(catDragOver!==idx) setCatDragOver(idx); }}
                    onDragLeave={() => setCatDragOver(null)}
                    onDrop={e => { e.preventDefault(); if(catDragIdx===null||catDragIdx===idx)return; const r=[...filteredCats]; const[m]=r.splice(catDragIdx,1); r.splice(idx,0,m); setCatDragIdx(null); setCatDragOver(null); saveCatOrder(r); }}
                    onDragEnd={() => { setCatDragIdx(null); setCatDragOver(null); }}
                    onClick={() => setSelCat(cat)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', borderRadius:9, cursor:'grab', marginBottom:5, border:'1.5px solid '+(isDrgOv?'#4D7C0F':selCat?._id===cat._id?'#4D7C0F':'#E8F5E9'), background:isDrg?'#f0f0f0':isDrgOv?'#F0FDF4':selCat?._id===cat._id?'#F0FDF4':'white', opacity:isDrg?.4:cat.isActive===false?.5:1, transform:isDrgOv?'scale(1.02)':'scale(1)', transition:'all .15s' }}>
                    <span style={{ color:'#bbb', fontSize:12, flexShrink:0 }}>⠿</span>
                    <div style={{ width:32, height:32, borderRadius:7, background:'#E8F5E9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{cat.emoji||''}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#1B5E20', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cat.label||cat.name}</div>
                      <div style={{ fontSize:11, color:'#9E9E9E' }}>{cat.subCategories?.length||0} items</div>
                    </div>
                    {cat.isActive===false && <span className="badge badge--danger" style={{ fontSize:9 }}>OFF</span>}
                  </div>
                  );
                })
              }
            </div>

            {/* Right: Selected category */}
            {selCat ? (
              <div>
                {/* Header */}
                <div className="card" style={{ marginBottom:12 }}>
                  <div className="card__body--sm flex-between">
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:40, height:40, borderRadius:9, background:'#E8F5E9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, border:'1px solid #C8E6C9' }}>{selCat.emoji||''}</div>
                      <div>
                        <div style={{ fontSize:15, fontWeight:700, color:'#1B5E20' }}>{selCat.label||selCat.name}</div>
                        <div style={{ fontSize:12, color:'#9E9E9E' }}>{selCat.subCategories?.length||0} sub-categories</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn--outline btn--sm" onClick={() => setEditCat({...selCat})}>✏️ Edit</button>
                      <button className={'btn btn--sm '+(selCat.isActive===false?'btn--success':'btn--warning')} onClick={() => toggleCat(selCat)}>
                        {selCat.isActive===false ? '✅ Enable' : '— Disable'}
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => deleteCat(selCat)}>🗑</button>
                    </div>
                  </div>
                </div>

                {/* Subcategories */}
                <div className="card">
                  <div className="card__body--sm">
                    <div className="flex-between" style={{ marginBottom:10 }}>
                      <span style={{ fontWeight:600, fontSize:13, color:'#1B5E20' }}>🏷️ Sub-categories</span>
                      <button className="btn btn--primary btn--sm" onClick={() => setShowAddSub(true)}>＋ Add</button>
                    </div>
                    <input value={searchSub} onChange={e=>setSearchSub(e.target.value)} placeholder="Search sub-categories..."
                      style={{ width:'100%', padding:'7px 10px', border:'1px solid #dee2e6', borderRadius:7, fontSize:12, outline:'none', marginBottom:10, boxSizing:'border-box' }} />

                    {filteredSubs.length === 0 ? (
                      <div className="empty-state" style={{ padding:'30px 0' }}>
                        <div className="empty-state__icon" style={{ fontSize:28 }}></div>
                        <div className="empty-state__title">No subcategories yet</div>
                        <button className="btn btn--primary btn--sm" style={{ marginTop:8 }} onClick={() => setShowAddSub(true)}>Add first one</button>
                      </div>
                    ) : (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                        {filteredSubs.map((sub, idx) => {
                          const isDrg = subDragIdx===idx, isDrgOv = subDragOver===idx;
                          const realIdx = (selCat?.subCategories||[]).findIndex(s=>s._id===sub._id);
                          return (
                          <div key={sub._id}
                            draggable
                            onDragStart={e => { e.dataTransfer.effectAllowed='move'; setSubDragIdx(idx); }}
                            onDragOver={e => { e.preventDefault(); if(subDragOver!==idx) setSubDragOver(idx); }}
                            onDragLeave={() => setSubDragOver(null)}
                            onDrop={e => { e.preventDefault(); if(subDragIdx===null||subDragIdx===idx)return; const s=[...filteredSubs]; const[m]=s.splice(subDragIdx,1); s.splice(idx,0,m); setSubDragIdx(null); setSubDragOver(null); saveSubOrder(s); }}
                            onDragEnd={() => { setSubDragIdx(null); setSubDragOver(null); }}
                            style={{ background:isDrg?'#f0f0f0':isDrgOv?'#F0FDF4':'#FAFFF8', borderRadius:8, border:'1.5px solid '+(isDrgOv?'#4D7C0F':'#E8F5E9'), padding:'10px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'grab', opacity:isDrg?.4:1, transform:isDrgOv?'scale(1.02)':'scale(1)', transition:'all .15s' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                              <span style={{ color:'#bbb', fontSize:11 }}>⠿</span>
                              <div>
                                <div style={{ fontSize:12, fontWeight:600, color:'#1B5E20' }}>{sub.name}</div>
                                {sub.image && <img src={sub.image} alt="" style={{ width:32, height:32, borderRadius:5, objectFit:'cover', marginTop:4 }} />}
                              </div>
                            </div>
                            <div style={{ display:'flex', gap:4 }}>
                              <button onClick={() => { if(realIdx>0){const s=[...(selCat.subCategories||[])];[s[realIdx-1],s[realIdx]]=[s[realIdx],s[realIdx-1]];saveSubOrder(s);} }} style={{ background:'none', border:'1px solid #E8F5E9', borderRadius:5, padding:'2px 6px', cursor:'pointer', color:'#9E9E9E', fontSize:13 }}>↑</button>
                              <button onClick={() => { const s=[...(selCat.subCategories||[])];if(realIdx<s.length-1){[s[realIdx],s[realIdx+1]]=[s[realIdx+1],s[realIdx]];saveSubOrder(s);} }} style={{ background:'none', border:'1px solid #E8F5E9', borderRadius:5, padding:'2px 6px', cursor:'pointer', color:'#9E9E9E', fontSize:13 }}>↓</button>
                              <button className="btn btn--outline btn--sm" style={{ padding:'3px 7px', fontSize:12 }} onClick={() => setEditSub({...sub})}>✏️</button>
                              <button className="btn btn--danger btn--sm" style={{ padding:'3px 7px', fontSize:12 }} onClick={() => deleteSubCategory(sub)}>🗑</button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state"><div className="empty-state__icon">🗂️</div><div className="empty-state__title">Select a category</div></div>
            )}
          </div>
        </>
      )}

      {/* ══ GUIDE TAB ════════════════════════════════════════════ */}
      {tab === 'guide' && (
        <div className="card"><div className="card__body">
          {[
            { step:1, title:'Add nursery details', desc:'Enter nursery name, owner, mobile, address and specialties. This shows on the public directory.' },
            { step:2, title:'Verify the nursery',  desc:'Once you confirm the nursery is real, click "Verify". A verified badge shows on their listing.' },
            { step:3, title:'Manage categories',   desc:'Use the Category List tab to add/edit plant categories and subcategories used across the platform.' },
            { step:4, title:'Nursery adds their plants', desc:'They add plant name, category, stage, quantity and price. Stock auto-updates to In stock / Low / Out.' },
            { step:5, title:'Users see the directory', desc:'On zevazeva.com users see all nurseries. They tap View Plants to see available stock and call to order.' },
          ].map(item => (
            <div key={item.step} style={{ display:'flex', gap:12, marginBottom:14 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:'#4D7C0F', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0 }}>{item.step}</div>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:'#1a2e05' }}>{item.title}</div>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:2, lineHeight:1.5 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div></div>
      )}

      {/* ══ MODALS ════════════════════════════════════════════════ */}

      {/* Edit Nursery Modal */}
      {editNursery && (
        <div className="modal-overlay" onClick={() => setEditNursery(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal__header">
              <div className="modal__title">✏️ Edit — {editNursery.name}</div>
              <button className="modal__close" onClick={() => setEditNursery(null)}>×</button>
            </div>
            <div className="modal__body">
              {[
                { k:'name',      label:'Nursery Name *', ph:'e.g. Green Valley Nursery' },
                { k:'ownerName', label:'Owner Name *',   ph:'Full name' },
                { k:'mobile',    label:'Mobile',         ph:'10-digit', type:'tel' },
                { k:'address',   label:'Address',        ph:'No, Street, Area' },
                { k:'city',      label:'City',           ph:'e.g. Bangalore' },
                { k:'pincode',   label:'Pincode',        ph:'6-digit' },
                { k:'timings',   label:'Working Hours',  ph:'7am – 7pm, Mon–Sat' },
              ].map(f => (
                <div className="form-group" key={f.k}>
                  <label className="label">{f.label}</label>
                  <input className="input" type={f.type||'text'} placeholder={f.ph}
                    value={editForm[f.k]||''} onChange={e => setEditForm(p=>({...p,[f.k]:e.target.value}))} />
                </div>
              ))}
              <div className="form-group">
                <label className="label">Description</label>
                <textarea className="input" rows={2} value={editForm.description||''}
                  onChange={e => setEditForm(p=>({...p,description:e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="label">Specialties</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {SPECIALTIES.map(s => (
                    <button key={s} type="button"
                      onClick={() => setEditForm(p => ({...p, specialties: p.specialties?.includes(s) ? p.specialties.filter(x=>x!==s) : [...(p.specialties||[]), s]}))}
                      style={{ padding:'5px 12px', borderRadius:20, border:'1.5px solid '+((editForm.specialties||[]).includes(s)?'#4D7C0F':'#dee2e6'), background:(editForm.specialties||[]).includes(s)?'#f1fae8':'white', color:(editForm.specialties||[]).includes(s)?'#4D7C0F':'#6C757D', fontSize:12, cursor:'pointer', textTransform:'capitalize' }}>
                      {CAT_ICONS[s]} {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setEditNursery(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={saveEditNursery} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Nursery */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal__header"><div className="modal__title">➕ Add New Nursery</div><button className="modal__close" onClick={() => setShowAdd(false)}>×</button></div>
            <div className="modal__body">
              {[
                { k:'name',        label:'Nursery Name *',     ph:'e.g. Green Valley Nursery' },
                { k:'ownerName',   label:'Owner Name *',       ph:'Full name' },
                { k:'mobile',      label:'Mobile Number *',    ph:'10-digit', type:'tel' },
                { k:'alternateMobile',label:'Alternate Mobile',ph:'Optional' },
                { k:'address',     label:'Full Address *',     ph:'No, Street, Area, District' },
                { k:'city',        label:'City',               ph:'e.g. Bangalore' },
                { k:'pincode',     label:'Pincode',            ph:'6-digit' },
                { k:'timings',     label:'Working Hours',      ph:'7am – 7pm, Mon–Sat' },
                { k:'adminMobile', label:'Admin Panel Mobile', ph:'For nursery admin login' },
              ].map(f => (
                <div className="form-group" key={f.k}>
                  <label className="label">{f.label}</label>
                  <input className="input" type={f.type||'text'} placeholder={f.ph} value={form[f.k]} onChange={e => setForm(p=>({...p,[f.k]:e.target.value}))} />
                </div>
              ))}
              <div className="form-group">
                <label className="label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" checked={form.hasAdminPanel} onChange={e=>setForm(p=>({...p,hasAdminPanel:e.target.checked}))} style={{ width:16, height:16 }} />
                  This nursery has their own admin panel
                </label>
              </div>
              <div className="form-group">
                <label className="label">Description</label>
                <textarea className="input" rows={2} value={form.description} onChange={e=>setF('description',e.target.value)} />
              </div>
              <div className="form-group">
                <label className="label">Specialties</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {SPECIALTIES.map(s => (
                    <button key={s} onClick={() => toggleSpecialty(s)} type="button"
                      style={{ padding:'5px 12px', borderRadius:20, border:'1.5px solid '+(form.specialties.includes(s)?'#4D7C0F':'#dee2e6'), background:form.specialties.includes(s)?'#f1fae8':'white', color:form.specialties.includes(s)?'#4D7C0F':'#6C757D', fontSize:12, cursor:'pointer', textTransform:'capitalize' }}>
                      {CAT_ICONS[s]} {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={addNursery} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : 'Add Nursery'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Plant */}
      {showAddPlant && selected && (
        <div className="modal-overlay" onClick={() => setShowAddPlant(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal__header">
              <div className="modal__title">{editPlant ? '✏️ Edit Plant' : ' Add Plant — ' + selected.name}</div>
              <button className="modal__close" onClick={() => setShowAddPlant(false)}>×</button>
            </div>
            <div className="modal__body">
              {/* Image upload */}
              <div className="form-group">
                <label className="label">Plant Photo</label>
                <input ref={plantImgRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => uploadPlantImg(e.target.files[0])} />
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <div style={{ width:56, height:56, borderRadius:8, background:'#F0FDF4', border:'1px solid #BBF7D0', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>
                    {plantForm.image ? <img src={plantForm.image} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : ''}
                  </div>
                  <button className="btn btn--outline btn--sm" onClick={() => plantImgRef.current?.click()} type="button">
                    {uploadingImg ? 'Uploading...' : '📷 Upload Photo'}
                  </button>
                </div>
              </div>
              <div className="form-group"><label className="label">Plant Name *</label>
                <input className="input" placeholder="e.g. Tomato seedlings" autoFocus value={plantForm.name} onChange={e=>setPlantForm(p=>({...p,name:e.target.value}))} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group"><label className="label">Category</label>
                  <select className="input" value={plantForm.category} onChange={e=>setPlantForm(p=>({...p,category:e.target.value}))}>
                    {PLANT_CATS.map(c => <option key={c} value={c} style={{ textTransform:'capitalize' }}>{CAT_ICONS[c]} {c}</option>)}
                  </select></div>
                <div className="form-group"><label className="label">Stage / Notes</label>
                  <input className="input" placeholder="e.g. 3-4 leaf" value={plantForm.stage} onChange={e=>setPlantForm(p=>({...p,stage:e.target.value}))} /></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group"><label className="label">Quantity</label>
                  <input className="input" type="number" min={0} value={plantForm.quantity} onChange={e=>setPlantForm(p=>({...p,quantity:parseInt(e.target.value)||0}))} /></div>
                <div className="form-group"><label className="label">Unit</label>
                  <select className="input" value={plantForm.unit} onChange={e=>setPlantForm(p=>({...p,unit:e.target.value}))}>
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group"><label className="label">Price (₹)</label>
                  <input className="input" type="number" min={0} value={plantForm.price} onChange={e=>setPlantForm(p=>({...p,price:parseFloat(e.target.value)||0}))} /></div>
                <div className="form-group"><label className="label">Price per</label>
                  <input className="input" placeholder="per plant" value={plantForm.priceUnit} onChange={e=>setPlantForm(p=>({...p,priceUnit:e.target.value}))} /></div>
              </div>
              <div className="form-group"><label className="label">Description</label>
                <textarea className="input" rows={2} value={plantForm.description} onChange={e=>setPlantForm(p=>({...p,description:e.target.value}))} /></div>
              <div style={{ padding:'8px 12px', borderRadius:8, background:plantForm.quantity<=0?'#FEE2E2':plantForm.quantity<20?'#FEF9C3':'#DCFCE7', fontSize:12, fontWeight:600, color:plantForm.quantity<=0?'#991B1B':plantForm.quantity<20?'#854D0E':'#166534' }}>
                Status: {plantForm.quantity<=0?'Out of Stock':plantForm.quantity<20?'Low Stock':'In Stock'}
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setShowAddPlant(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={savePlant} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : editPlant ? '✅ Update Plant' : '✅ Add Plant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category */}
      {showAddCat && (
        <div className="modal-overlay" onClick={() => setShowAddCat(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal__header"><div className="modal__title">➕ Add Category</div><button className="modal__close" onClick={() => setShowAddCat(false)}>×</button></div>
            <div className="modal__body">
              <div className="form-group"><label className="label">Category Name *</label>
                <input className="input" autoFocus value={newCat.name} onChange={e=>setNewCat(p=>({...p,name:e.target.value}))} placeholder="e.g. Flowers" /></div>
              <div className="form-group"><label className="label">Emoji</label>
                <input className="input" value={newCat.emoji} onChange={e=>setNewCat(p=>({...p,emoji:e.target.value}))} style={{ width:80 }} /></div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setShowAddCat(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={addCategory} disabled={saving}>{saving?'Adding...':'✅ Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category */}
      {editCat && (
        <div className="modal-overlay" onClick={() => setEditCat(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal__header"><div className="modal__title">✏️ Edit Category</div><button className="modal__close" onClick={() => setEditCat(null)}>×</button></div>
            <div className="modal__body">
              <div className="form-group"><label className="label">Name *</label>
                <input className="input" autoFocus value={editCat.label||''} onChange={e=>setEditCat(p=>({...p,label:e.target.value}))} /></div>
              <div className="form-group"><label className="label">Emoji</label>
                <input className="input" value={editCat.emoji||''} onChange={e=>setEditCat(p=>({...p,emoji:e.target.value}))} style={{ width:80 }} /></div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setEditCat(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={updateCategory} disabled={saving}>{saving?'Saving...':'✅ Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subcategory */}
      {showAddSub && (
        <div className="modal-overlay" onClick={() => setShowAddSub(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal__header"><div className="modal__title">➕ Add Sub-category</div><button className="modal__close" onClick={() => setShowAddSub(false)}>×</button></div>
            <div className="modal__body">
              <div style={{ fontSize:12, color:'#6C757D', marginBottom:10 }}>Under: <strong>{selCat?.label||selCat?.name}</strong></div>
              <div className="form-group"><label className="label">Name *</label>
                <input className="input" autoFocus value={newSub.name} onChange={e=>setNewSub(p=>({...p,name:e.target.value}))} placeholder="e.g. Tomato" /></div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setShowAddSub(false)}>Cancel</button>
              <button className="btn btn--primary" onClick={addSubCategory} disabled={saving}>{saving?'Adding...':'✅ Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subcategory */}
      {editSub && (
        <div className="modal-overlay" onClick={() => setEditSub(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal__header"><div className="modal__title">✏️ Edit Sub-category</div><button className="modal__close" onClick={() => setEditSub(null)}>×</button></div>
            <div className="modal__body">
              <div className="form-group"><label className="label">Name *</label>
                <input className="input" autoFocus value={editSub.name||''} onChange={e=>setEditSub(p=>({...p,name:e.target.value}))} /></div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setEditSub(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={updateSubCategory} disabled={saving}>{saving?'Saving...':'✅ Save'}</button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}